/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getResumeIndicatorC3,
	ResumeIndicatorC3Data,
} from "@/services/esus/indicator/esf/c3/getResumeIndicatorC3";
import {
	getResumeIndicatorC3ByHealthTeam,
	ResumeIndicatorC3ByHealthTeamData,
} from "@/services/esus/indicator/esf/c3/getResumeIndicatorC3ByHealthTeam";
import {
	DetailedCitizenScoresIndicatorC3Data,
	getDetailedCitizensScoresIndicatorC3,
} from "@/services/esus/indicator/esf/c3/getDetailedCitizensScoresIndicatorC3";
import {
	DetailedCitizenAcsVisitsIndicatorC3Data,
	getDetailedCitizensAcsVisitsIndicatorC3,
} from "@/services/esus/indicator/esf/c3/getDetailedCitizensAcsVisitsIndicatorC3";
import {
	DetailedCitizenVaccinationsIndicatorC3Data,
	getDetailedCitizensVaccinationsIndicatorC3,
} from "@/services/esus/indicator/esf/c3/getDetailedCitizensVaccinationsIndicatorC3";
import {
	DetailedCitizenAttendancesIndicatorC3Data,
	getDetailedCitizensAttendancesIndicatorC3,
} from "@/services/esus/indicator/esf/c3/getDetailedCitizensAttendancesIndicatorC3";
import {
	DetailedCitizenProceduresIndicatorC3Data,
	getDetailedCitizensProceduresIndicatorC3,
} from "@/services/esus/indicator/esf/c3/getDetailedCitizensProceduresIndicatorC3";

import { IndicatorEsfC3Resume } from "./IndicatorEsfC3Resume";
import { IndicatorEsfC3CitizensAttendances } from "./IndicatorEsfC3CitizensAttendances";
import { IndicatorEsfC3HealthTeams } from "./IndicatorEsfC3HealthTeams";
import { IndicatorEsfC3CitizensScores } from "./IndicatorEsfC3CitizensScores";
import { IndicatorEsfC3CitizensVaccinations } from "./IndicatorEsfC3CitizensVaccinations";
import { IndicatorEsfC3CitizensAcsVisits } from "./IndicatorEsfC3CitizensAcsVisits";
import { IndicatorEsfC3CitizensProcedures } from "./IndicatorEsfC3CitizensProcedures";

type IndicatorEsfC3Props = {
	selectedQuarter: string;
	selectedYear: number;
	selectedMonth: number;
	selectedIne?: string;
	isPendingToUpdate: boolean;
	setIsPendingToUpdate: (isPendingToUpdate: boolean) => void;
};

export function IndicatorEsfC3({
	selectedQuarter,
	selectedYear,
	selectedMonth,
	selectedIne,
	isPendingToUpdate,
	setIsPendingToUpdate,
}: Readonly<IndicatorEsfC3Props>) {
	const { setTitle } = useLayout();
	const { uf, ibgeCode } = useCurrentAccount();
	const pageSize = 10;
	const [resumeIndicatorC3, setResumeIndicatorC3] = useState<ResumeIndicatorC3Data>(
		new ResumeIndicatorC3Data()
	);

	const [pageNumberHealthTeamsClassifications, setPageNumberHealthTeamsClassifications] =
		useState(1);
	const [searchHealthTeamsClassification, setSearchHealthTeamsClassification] = useState("");
	const debouncedSearchHealthTeamsClassifications = useDebounce(
		searchHealthTeamsClassification,
		2000
	);
	const [paginatedHealthTeamsClassifications, setPaginatedHealthTeamsClassifications] = useState(
		new Paginated<ResumeIndicatorC3ByHealthTeamData>()
	);
	const [isLoadingHealthTeamsClassifications, setIsLoadingHealthTeamsClassifications] =
		useState(false);

	const [pageNumberCitizensScores, setPageNumberCitizensScores] = useState(1);
	const [searchCitizensScore, setSearchCitizensScore] = useState("");
	const [isEvaluatedCitizensScore, setIsEvaluatedCitizensScore] = useState<boolean>();
	const debouncedSearchCitizensScore = useDebounce(searchCitizensScore, 2000);
	const [paginatedCitizensScores, setPaginatedCitizensScores] = useState(
		new Paginated<DetailedCitizenScoresIndicatorC3Data>()
	);
	const [isLoadingCitizensScores, setIsLoadingCitizensScores] = useState(false);

	const [pageNumberAttendances, setPageNumberAttendances] = useState(1);
	const [searchCitizensWithAttendance, setSearchCitizensWithAttendance] = useState("");
	const [isEvaluatedCitizensWithAttendance, setIsEvaluatedCitizensWithAttendance] =
		useState<boolean>();
	const debouncedSearchAttendance = useDebounce(searchCitizensWithAttendance, 2000);
	const [paginatedCitizensWithAttendances, setPaginatedCitizensWithAttendances] = useState(
		new Paginated<DetailedCitizenAttendancesIndicatorC3Data>()
	);
	const [isLoadingAttendances, setIsLoadingAttendances] = useState(false);

	const [pageNumberCitizensProcedures, setPageNumberCitizensProcedures] = useState(1);
	const [searchCitizensProcedure, setSearchCitizensProcedure] = useState("");
	const [isEvaluatedCitizensProcedure, setIsEvaluatedCitizensProcedure] = useState<boolean>();
	const debouncedSearchCitizensProcedure = useDebounce(searchCitizensProcedure, 2000);
	const [paginatedCitizensProcedures, setPaginatedCitizensProcedures] = useState(
		new Paginated<DetailedCitizenProceduresIndicatorC3Data>()
	);
	const [isLoadingCitizensProcedures, setIsLoadingCitizensProcedures] = useState(false);

	const [pageNumberCitizensAcsVisits, setPageNumberCitizensAcsVisits] = useState(1);
	const [searchCitizensAcsVisit, setSearchCitizensAcsVisit] = useState("");
	const [isEvaluatedCitizensAcsVisit, setIsEvaluatedCitizensAcsVisit] = useState<boolean>();
	const debouncedSearchCitizensAcsVisit = useDebounce(searchCitizensAcsVisit, 2000);
	const [paginatedCitizensAcsVisits, setPaginatedCitizensAcsVisits] = useState(
		new Paginated<DetailedCitizenAcsVisitsIndicatorC3Data>()
	);
	const [isLoadingCitizensAcsVisits, setIsLoadingCitizensAcsVisits] = useState(false);

	const [pageNumberCitizensVaccinations, setPageNumberCitizensVaccinations] = useState(1);
	const [searchCitizensVaccination, setSearchCitizensVaccination] = useState("");
	const [isEvaluatedCitizensVaccination, setIsEvaluatedCitizensVaccination] = useState<boolean>();
	const debouncedSearchCitizensVaccination = useDebounce(searchCitizensVaccination, 2000);
	const [paginatedCitizensVaccinations, setPaginatedCitizensVaccinations] = useState(
		new Paginated<DetailedCitizenVaccinationsIndicatorC3Data>()
	);
	const [isLoadingCitizensVaccinations, setIsLoadingCitizensVaccinations] = useState(false);

	async function handleFilter() {
		await Promise.all([
			handleGetResumeIndicatorC3(),
			handleGetResumeIndicatorC3ByHealthTeam(),
			handleGetDetailedCitizensScoresIndicatorC3(),
			handleGetDetailedCitizensWithAttendancesIndicatorC3(),
			handleGetDetailedCitizensProceduresIndicatorC3(),
			handleGetDetailedCitizensAcsVisitsIndicatorC3(),
			handleGetDetailedCitizensVaccinationsIndicatorC3(),
		]);
		setIsPendingToUpdate(false);
	}

	async function handleGetResumeIndicatorC3() {
		setResumeIndicatorC3(
			await getResumeIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
			})
		);
	}

	async function handleGetResumeIndicatorC3ByHealthTeam(pageNumber?: number) {
		setIsLoadingHealthTeamsClassifications(true);
		setPaginatedHealthTeamsClassifications(
			await getResumeIndicatorC3ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingHealthTeamsClassifications(false);
	}

	async function handleGetAllResumeIndicatorC3ByHealthTeam() {
		return (
			await getResumeIndicatorC3ByHealthTeam({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchHealthTeamsClassification,
				pageNumber: 1,
				pageSize: paginatedHealthTeamsClassifications.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensScoresIndicatorC3(pageNumber?: number) {
		setIsLoadingCitizensScores(true);
		setPaginatedCitizensScores(
			await getDetailedCitizensScoresIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensScores(false);
	}

	async function handleGetAllDetailedCitizensScoresIndicatorC3() {
		return (
			await getDetailedCitizensScoresIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensScore,
				isEvaluated: isEvaluatedCitizensScore,
				pageNumber: 1,
				pageSize: paginatedCitizensScores.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensWithAttendancesIndicatorC3(pageNumber?: number) {
		setIsLoadingAttendances(true);
		setPaginatedCitizensWithAttendances(
			await getDetailedCitizensAttendancesIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingAttendances(false);
	}

	async function handleGetAllDetailedCitizensWithAttendancesIndicatorC3() {
		return (
			await getDetailedCitizensAttendancesIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensWithAttendance,
				isEvaluated: isEvaluatedCitizensWithAttendance,
				pageNumber: 1,
				pageSize: paginatedCitizensWithAttendances.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensProceduresIndicatorC3(pageNumber?: number) {
		setIsLoadingCitizensProcedures(true);
		setPaginatedCitizensProcedures(
			await getDetailedCitizensProceduresIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensProcedure,
				isEvaluated: isEvaluatedCitizensProcedure,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensProcedures(false);
	}

	async function handleGetAllDetailedCitizensProceduresIndicatorC3() {
		return (
			await getDetailedCitizensProceduresIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensProcedure,
				isEvaluated: isEvaluatedCitizensProcedure,
				pageNumber: 1,
				pageSize: paginatedCitizensProcedures.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensAcsVisitsIndicatorC3(pageNumber?: number) {
		setIsLoadingCitizensAcsVisits(true);
		setPaginatedCitizensAcsVisits(
			await getDetailedCitizensAcsVisitsIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensAcsVisits(false);
	}

	async function handleGetAllDetailedCitizensAcsVisitsIndicatorC3() {
		return (
			await getDetailedCitizensAcsVisitsIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensAcsVisit,
				isEvaluated: isEvaluatedCitizensAcsVisit,
				pageNumber: 1,
				pageSize: paginatedCitizensAcsVisits.totalRecords,
				export: true,
			})
		).data;
	}

	async function handleGetDetailedCitizensVaccinationsIndicatorC3(pageNumber?: number) {
		setIsLoadingCitizensVaccinations(true);
		setPaginatedCitizensVaccinations(
			await getDetailedCitizensVaccinationsIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				pageNumber: pageNumber ?? 1,
				pageSize,
			})
		);
		setIsLoadingCitizensVaccinations(false);
	}

	async function handleGetAllDetailedCitizensVaccinationsIndicatorC3() {
		return (
			await getDetailedCitizensVaccinationsIndicatorC3({
				uf,
				ibgeCode,
				quarter: selectedQuarter,
				year: selectedYear,
				month: selectedMonth,
				ine: selectedIne,
				search: searchCitizensVaccination,
				isEvaluated: isEvaluatedCitizensVaccination,
				pageNumber: 1,
				pageSize: paginatedCitizensVaccinations.totalRecords,
				export: true,
			})
		).data;
	}

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetResumeIndicatorC3ByHealthTeam(pageNumberHealthTeamsClassifications);
	}, [pageNumberHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberHealthTeamsClassifications === 1) {
				handleGetResumeIndicatorC3ByHealthTeam(pageNumberHealthTeamsClassifications);
			} else {
				setPageNumberHealthTeamsClassifications(1);
			}
		}
	}, [debouncedSearchHealthTeamsClassifications]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensScoresIndicatorC3(pageNumberCitizensScores);
	}, [pageNumberCitizensScores]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensScores === 1) {
				handleGetDetailedCitizensScoresIndicatorC3(pageNumberCitizensScores);
			} else {
				setPageNumberCitizensScores(1);
			}
		}
	}, [debouncedSearchCitizensScore, isEvaluatedCitizensScore]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensWithAttendancesIndicatorC3(pageNumberAttendances);
	}, [pageNumberAttendances]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberAttendances === 1) {
				handleGetDetailedCitizensWithAttendancesIndicatorC3(pageNumberAttendances);
			} else {
				setPageNumberAttendances(1);
			}
		}
	}, [debouncedSearchAttendance, isEvaluatedCitizensWithAttendance]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensProceduresIndicatorC3(pageNumberCitizensProcedures);
	}, [pageNumberCitizensProcedures]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensProcedures === 1) {
				handleGetDetailedCitizensProceduresIndicatorC3(pageNumberCitizensProcedures);
			} else {
				setPageNumberCitizensProcedures(1);
			}
		}
	}, [debouncedSearchCitizensProcedure, isEvaluatedCitizensProcedure]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensAcsVisitsIndicatorC3(pageNumberCitizensAcsVisits);
	}, [pageNumberCitizensAcsVisits]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensAcsVisits === 1) {
				handleGetDetailedCitizensAcsVisitsIndicatorC3(pageNumberCitizensAcsVisits);
			} else {
				setPageNumberCitizensAcsVisits(1);
			}
		}
	}, [debouncedSearchCitizensAcsVisit, isEvaluatedCitizensAcsVisit]);

	useEffect(() => {
		if (!isPendingToUpdate)
			handleGetDetailedCitizensVaccinationsIndicatorC3(pageNumberCitizensVaccinations);
	}, [pageNumberCitizensVaccinations]);

	useEffect(() => {
		if (!isPendingToUpdate) {
			if (pageNumberCitizensVaccinations === 1) {
				handleGetDetailedCitizensVaccinationsIndicatorC3(pageNumberCitizensVaccinations);
			} else {
				setPageNumberCitizensVaccinations(1);
			}
		}
	}, [debouncedSearchCitizensVaccination, isEvaluatedCitizensVaccination]);

	useEffect(() => {
		if (isPendingToUpdate) handleFilter();
	}, [isPendingToUpdate]);

	useEffect(() => {
		setTitle("C3 - Cuidado da Gestante e Puérpera");
	}, [setTitle]);

	return (
		<>
			<IndicatorEsfC3Resume resume={resumeIndicatorC3} />
			<IndicatorEsfC3HealthTeams
				paginatedHealthTeamsClassifications={paginatedHealthTeamsClassifications}
				isLoading={isLoadingHealthTeamsClassifications}
				search={searchHealthTeamsClassification}
				setSearch={setSearchHealthTeamsClassification}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberHealthTeamsClassifications}
				setPageNumber={setPageNumberHealthTeamsClassifications}
				handleGetAllResumeIndicatorC3ByHealthTeam={
					handleGetAllResumeIndicatorC3ByHealthTeam
				}
			/>
			<IndicatorEsfC3CitizensScores
				paginatedCitizensScores={paginatedCitizensScores}
				isLoading={isLoadingCitizensScores}
				search={searchCitizensScore}
				setSearch={setSearchCitizensScore}
				isEvaluated={isEvaluatedCitizensScore}
				setIsEvaluated={setIsEvaluatedCitizensScore}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensScores}
				setPageNumber={setPageNumberCitizensScores}
				handleGetAllDetailedCitizensScoresIndicatorC3={
					handleGetAllDetailedCitizensScoresIndicatorC3
				}
			/>
			<IndicatorEsfC3CitizensAttendances
				paginatedAttendances={paginatedCitizensWithAttendances}
				isLoading={isLoadingAttendances}
				search={searchCitizensWithAttendance}
				setSearch={setSearchCitizensWithAttendance}
				isEvaluated={isEvaluatedCitizensWithAttendance}
				setIsEvaluated={setIsEvaluatedCitizensWithAttendance}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberAttendances}
				setPageNumber={setPageNumberAttendances}
				handleGetAllDetailedIndicatorC3={
					handleGetAllDetailedCitizensWithAttendancesIndicatorC3
				}
			/>
			<IndicatorEsfC3CitizensProcedures
				paginatedCitizensProcedures={paginatedCitizensProcedures}
				isLoading={isLoadingCitizensProcedures}
				search={searchCitizensProcedure}
				setSearch={setSearchCitizensProcedure}
				isEvaluated={isEvaluatedCitizensProcedure}
				setIsEvaluated={setIsEvaluatedCitizensProcedure}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensProcedures}
				setPageNumber={setPageNumberCitizensProcedures}
				handleGetAllDetailedCitizensProceduresIndicatorC3={
					handleGetAllDetailedCitizensProceduresIndicatorC3
				}
			/>
			<IndicatorEsfC3CitizensAcsVisits
				paginatedCitizensAcsVisits={paginatedCitizensAcsVisits}
				isLoading={isLoadingCitizensAcsVisits}
				search={searchCitizensAcsVisit}
				setSearch={setSearchCitizensAcsVisit}
				isEvaluated={isEvaluatedCitizensAcsVisit}
				setIsEvaluated={setIsEvaluatedCitizensAcsVisit}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensAcsVisits}
				setPageNumber={setPageNumberCitizensAcsVisits}
				handleGetAllDetailedCitizensAcsVisitsIndicatorC3={
					handleGetAllDetailedCitizensAcsVisitsIndicatorC3
				}
			/>
			<IndicatorEsfC3CitizensVaccinations
				paginatedCitizensVaccinations={paginatedCitizensVaccinations}
				isLoading={isLoadingCitizensVaccinations}
				search={searchCitizensVaccination}
				setSearch={setSearchCitizensVaccination}
				isEvaluated={isEvaluatedCitizensVaccination}
				setIsEvaluated={setIsEvaluatedCitizensVaccination}
				selectedQuarter={selectedQuarter}
				pageNumber={pageNumberCitizensVaccinations}
				setPageNumber={setPageNumberCitizensVaccinations}
				handleGetAllDetailedCitizensVaccinationsIndicatorC3={
					handleGetAllDetailedCitizensVaccinationsIndicatorC3
				}
			/>
		</>
	);
}
